import { hasRuleValue, normalizeRuleText, uniqueNormalizedValues } from '../../shared/comparisons';

import type { CampaignSchedule, SetupRuleInput } from './types';

function toList(value?: Array<string> | string | null) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (!value) {
    return [];
  }

  return value
    .split(/[,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function isTrue(value?: boolean | string | null) {
  if (typeof value === 'boolean') {
    return value;
  }

  return ['true', 'yes', 'y', '1'].includes(normalizeRuleText(value));
}

export function statusFromCheck(check: boolean) {
  return check ? 'approved' : 'requires approval';
}

export function schedulesExcludeSelections(
  schedules: Array<CampaignSchedule> | undefined,
  excludedBrands: Array<string>,
  excludedCountries: Array<string>,
) {
  if (!schedules?.length) {
    return true;
  }

  const selectedBrands = new Set(excludedBrands.map(normalizeRuleText));
  const selectedCountries = new Set(excludedCountries.map(normalizeRuleText));

  return schedules.every((schedule) => {
    const scheduleBrand = normalizeRuleText(schedule.brand);
    const scheduleMarket = normalizeRuleText(schedule.market ?? schedule.region);

    return !selectedBrands.has(scheduleBrand) && !selectedCountries.has(scheduleMarket);
  });
}

export function buildSetupRuleContext(input: SetupRuleInput) {
  const excludedBrands = toList(input.exclusions?.brands);
  const excludedCountries = toList(input.exclusions?.countries);
  const costCurrency = input.opportunity?.costPriceCurrency;
  const packageCurrencies = uniqueNormalizedValues(input.packageCurrencies ?? []);
  const reportingCurrency = input.account?.currencyIsoCode;
  const uniqueComparedCurrencies = uniqueNormalizedValues([costCurrency, reportingCurrency, ...packageCurrencies]);
  const propertyCountry = normalizeRuleText(input.property?.country);
  const sourceErrors = input.sourceErrors ?? {};

  return {
    costCurrency,
    excludedBrands,
    excludedCountries,
    hasAbnError: !!sourceErrors.account || !!sourceErrors.property,
    hasAccountCurrencyError: !!sourceErrors.account || !!sourceErrors.opportunity,
    hasAccountNameError: !!sourceErrors.account || !!sourceErrors.property,
    hasCampaignExclusionsError: !!sourceErrors.opportunity,
    hasCurrencyError: !!sourceErrors.opportunity || !!sourceErrors.account,
    hasGeoError: !!sourceErrors.opportunity || !!sourceErrors.account,
    hasMarketingTermsError: !!sourceErrors.account || !!sourceErrors.parentAccount,
    hasPropertyCountry: hasRuleValue(input.property?.country),
    isAustralia: propertyCountry === 'australia' || propertyCountry === 'au',
    packageCurrencies,
    reportingCurrency,
    sourceErrors,
    uniqueComparedCurrencies,
  };
}
